// ByFundTab — the "By Fund" holdings view, matching the Participant Redesign
// standalone (portfolio3.jsx ByFundTab). One list renders every plan shape:
// funds-only, a managed model plus standalone funds, models-only, or a single
// investment. Model rows expand to reveal their underlying funds, indented, with a
// note naming the model they sit inside. Past 12 rows the list scrolls inside the
// card instead of stretching the page. Loan funds are never here — the adapter
// drops them, and the fallback path filters again for good measure.
//
// Two percentage columns, never one ambiguous "Alloc": Current Alloc is the share
// of today's balance, Future Alloc is the election for new money. Drift is the gap
// between them, drawn as a diverging bar from a centre axis and flagged past 5
// points. Plans that don't take elections read "n/a" and have no drift on record.
//
// Still a sortable DataTable with asset-class / fund-family filters and CSV export;
// filters collapse into a BottomSheet on mobile and a pinned total row closes it.
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, sortRows, PerfNum, Money, BottomSheet, Tooltip } from '../../../reusable';
import { useMediaQuery } from '../../../../hooks';
import { cx } from '../../../../utils/cx';
import { FunnelIcon, DownloadIcon } from '../../../icons/Icons';
import {
  balanceText,
  electionText,
  electionsApply,
  allocTotals,
  driftPts,
  driftText,
  countLabel,
  DRIFT_THRESHOLD_PCT,
} from '../portfolio.adapters';
import { treeOrFlat, flattenRows } from '../portfolio.models';
import { FilterDropdown } from './FilterDropdown';
import { ModelToggle, ModelTag, useModelExpand } from './ModelRow';
import './holdings-table.css';

const ALL = 'all';

// Rows past this count scroll inside the card (design: .pf3-table-rows.is-scroll).
const SCROLL_AFTER_ROWS = 12;

const sumBy = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
const round2 = (n) => Math.round(n * 100) / 100;
const round1 = (n) => Math.round(n * 10) / 10;

// CSV-safe cell: quote when the value contains a comma, quote or newline.
const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Name cell. Three shapes share the column: a model (toggle + name + tag), an
// underlying fund of an expanded model (indented), and a plain fund. On narrow
// screens fund rows swap the name for the ticker; the total row keeps its label.
function HoldingName({ value, row, open, onToggle, t }) {
  if (row._model) {
    return (
      <span className="holdings-fund holdings-fund--model">
        <ModelToggle open={open} name={row.name} onToggle={onToggle} t={t} />
        <span className="holdings-fund__sw" style={{ background: row.color }} />
        <span className="holdings-fund__nm">{value}</span>
        <ModelTag t={t} />
      </span>
    );
  }
  return (
    <span
      className={cx(
        'holdings-fund',
        row._total ? 'holdings-fund--total' : 'holdings-fund--swap',
        row._child && 'holdings-fund--child',
      )}
    >
      {row.color && <span className="holdings-fund__sw" style={{ background: row.color }} />}
      <span className="holdings-fund__nm">{value}</span>
      {!row._total && <span className="holdings-fund__tkr-inline">{row.ticker}</span>}
    </span>
  );
}
HoldingName.propTypes = {
  value: PropTypes.string,
  row: PropTypes.object,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
  t: PropTypes.func.isRequired,
};

// Drift cell — a diverging bar from a centre axis plus the signed value. Bars are
// scaled to the largest drift in view (floored at the threshold) so a portfolio
// sitting close to its elections reads as "barely moved" instead of being
// stretched to look dramatic.
//
// Past-threshold drift is emphasised IN THIS CELL ONLY — the bar and its value turn
// amber. The row itself is never tinted: a whole-row wash reads as "something is
// wrong with this holding" when the only thing being said is "this one column is
// past 5 points".
//
// No `title` here either. It put a native browser tooltip on every drift cell, so
// hovering anywhere in the table popped the same long sentence about rebalancing —
// eleven identical tooltips for one column-level fact.
function DriftCell({ value, max, hot }) {
  // `== null` and the finite check together, not `=== null`: the model NOTE row carries
  // no drift at all, so it arrived here as `undefined`, slipped past a null-only guard
  // and printed a literal "−NaN" in the column (invisible in the rendered row, but read
  // out by a screen reader and sized a bar from NaN).
  if (value == null || !Number.isFinite(value)) return <span className="holdings-drift__na">—</span>;
  // Half the track is all a bar can ever have: it grows from the centre axis, so 50% is
  // the edge of the cell. Clamped rather than trusted — the scale is computed from the
  // rows on show, and a caller that measures a different set (as this one once did) would
  // otherwise send a bar straight into the next column.
  const half = Math.min(50, (Math.abs(value) / max) * 50);
  return (
    <span className={cx('holdings-drift', hot && 'is-hot')}>
      <span className="holdings-drift__track">
        <i className="holdings-drift__axis" aria-hidden="true" />
        {value !== 0 && (
          <i
            className="holdings-drift__fill"
            aria-hidden="true"
            style={value > 0 ? { left: '50%', width: `${half}%` } : { left: `${50 - half}%`, width: `${half}%` }}
          />
        )}
      </span>
      <span className={cx('holdings-drift__val', value === 0 && 'is-zero')}>
        {value === 0 ? '0' : (value > 0 ? '+' : '−') + Math.abs(value)}
      </span>
    </span>
  );
}
DriftCell.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number.isRequired,
  /** Past the ±threshold — amber bar and value, this cell only. */
  hot: PropTypes.bool,
};

export function ByFundTab({ funds = [], tree, plan, summary, holdings, assetClasses = [], onPickFund, onPickModel }) {
  const { t } = useTranslation();
  // Filters collapse to the Filters button + bottom sheet at ≤700px (design parity:
  // portfolio3 ByFundTab uses useMaxWidth(700), matching the 600/900/1100 CSS steps).
  const mobile = useMediaQuery('(max-width: 700px)');
  const [sort, setSort] = useState({ key: 'balance', dir: 'desc' });
  const [classFilter, setClassFilter] = useState(ALL);
  const [familyFilter, setFamilyFilter] = useState(ALL);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isOpen: modelOpen, toggle: toggleModel } = useModelExpand();

  const takesElections = electionsApply(plan);

  const families = useMemo(
    () => Array.from(new Set(funds.map((f) => f.family).filter(Boolean))),
    [funds],
  );

  // The tree is the adapter's output; a flat fund list becomes one item per fund.
  const items = useMemo(() => treeOrFlat(tree, funds), [tree, funds]);

  // Filters apply to FUNDS, so a model survives through its matching members — and
  // when only some match, its totals are recomputed from what's left rather than
  // showing a total for funds the filter just hid.
  const filtered = useMemo(() => {
    const matches = (f) =>
      (classFilter === ALL || f.assetClassKey === classFilter) &&
      (familyFilter === ALL || f.family === familyFilter);

    return items.reduce((acc, it) => {
      if (it.type !== 'model') {
        if (matches(it)) acc.push(it);
        return acc;
      }
      const kept = (it.children || []).filter(matches);
      if (!kept.length) return acc;
      acc.push(
        kept.length === (it.children || []).length
          ? it
          : {
              ...it,
              children: kept,
              balance: round2(sumBy(kept, 'balance')),
              balancePct: round1(sumBy(kept, 'balancePct')),
              displayPct: sumBy(kept, 'displayPct'),
            },
      );
      return acc;
    }, []);
  }, [items, classFilter, familyFilter]);

  // Every visible fund, models flattened — what the count, the CSV and the
  // single-investment check read.
  const visibleFunds = useMemo(
    () =>
      filtered.flatMap((it) =>
        it.type === 'model' ? it.children.map((f) => ({ ...f, modelName: it.name })) : [it],
      ),
    [filtered],
  );
  const singleHolding = visibleFunds.length === 1;

  const activeCount = (classFilter !== ALL ? 1 : 0) + (familyFilter !== ALL ? 1 : 0);
  const unit = (n) => t(n === 1 ? 'portfolio.byFund.fund' : 'portfolio.byFund.funds');

  // Top-level rows, sorted. Drift rides along so the column sorts on the same
  // number it displays — by MAGNITUDE, since the question is "what's furthest off"
  // regardless of direction. Holdings with no election on record sort last.
  const topRows = useMemo(() => {
    const rows = filtered.map((it) => ({
      ...it,
      _model: it.type === 'model',
      drift: driftPts(it, plan),
    }));
    if (sort.key === 'drift') {
      const dir = sort.dir === 'asc' ? 1 : -1;
      return [...rows].sort((a, b) => {
        if (a.drift == null && b.drift == null) return 0;
        if (a.drift == null) return 1;
        if (b.drift == null) return -1;
        return (Math.abs(a.drift) - Math.abs(b.drift)) * dir;
      });
    }
    return sortRows(rows, sort);
  }, [filtered, sort, plan]);

  const totals = allocTotals(filtered, plan);

  // Pinned total row — values reuse the plan summary, not a re-derivation. A single
  // holding needs no total: it would just repeat the one row above it.
  const totalRow = {
    id: '__total',
    key: '__total',
    _total: true,
    name: t('portfolio.byFund.total'),
    ticker: '',
    assetClass: countLabel(filtered, t),
    color: null,
    balance: summary?.balance,
    balanceText: totals.balanceText,
    electionText: totals.electionText,
    drift: null,
    ytd: summary?.returns?.ytd?.pct,
    expense: holdings?.weightedExpense?.pct,
  };

  // Flatten to display rows: each top-level row, then — when a model is open — its
  // underlying funds and the note naming the model they sit inside.
  const rows = flattenRows(topRows, modelOpen, {
    child: (c) => ({ drift: driftPts(c, plan) }),
    note: (r) => ({
      id: `${r.key}__note`,
      key: `${r.key}__note`,
      name: t('portfolio.byFund.modelNote', { name: r.name, desc: r.desc || '' }).trim(),
    }),
  });
  if (!singleHolding) rows.push(totalRow);

  // Bar scale: the largest drift ON SHOW, never below the threshold — measured over the
  // FLATTENED rows, which is the fix for a bar that used to run out of its own column.
  // It was taken from `topRows`, so a model's underlying funds — rows that appear only
  // once the model is expanded, and that draw bars of their own — were not counted. A
  // child drifting further than anything at the top level therefore scaled to over half
  // the track, and the fill, being absolutely positioned, simply carried on into the
  // column beside it. `rows` is rebuilt whenever the expansion changes, so the scale now
  // moves with what is actually on screen.
  const maxDrift = Math.max(DRIFT_THRESHOLD_PCT, ...rows.map((r) => Math.abs(r.drift || 0)));

  const columns = [
    {
      key: 'name',
      label: t('portfolio.byFund.colFund'),
      render: (value, row) => (
        <HoldingName
          value={value}
          row={row}
          open={!!row._model && modelOpen(row.key)}
          onToggle={() => toggleModel(row.key)}
          t={t}
        />
      ),
    },
    {
      key: 'ticker',
      label: t('portfolio.byFund.colTicker'),
      sortable: false,
      // A model has no ticker of its own.
      render: (v, row) => (row._model ? '—' : v ? <span className="holdings-tkr">{v}</span> : null),
    },
    {
      key: 'assetClass',
      label: t('portfolio.byFund.colAssetClass'),
      sortable: false,
      render: (v) => (v ? <span className="holdings-td-class">{v}</span> : null),
    },
    {
      key: 'balance',
      label: t('portfolio.byFund.colBalance'),
      numeric: true,
      render: (v) => (v == null ? '—' : <Money value={v} />),
    },
    {
      key: 'balancePct',
      // Two-line header ("Current" / "Alloc") — the break lives in the translation
      // (\n) and .holdings-th--2line renders it, so each locale picks its own split.
      label: <span className="holdings-th--2line">{t('portfolio.byFund.colCurrentAlloc')}</span>,
      numeric: true,
      render: (v, row) => (
        <span className="holdings-balpct">{row._total ? row.balanceText : balanceText(row)}</span>
      ),
    },
    {
      key: 'electionPct',
      label: <span className="holdings-th--2line">{t('portfolio.byFund.colFutureAlloc')}</span>,
      numeric: true,
      render: (v, row) => {
        const text = row._total ? row.electionText : electionText(row, plan);
        return <span className={cx('holdings-elecpct', text === 'n/a' && 'is-soft')}>{text}</span>;
      },
    },
    {
      key: 'drift',
      // The design states the column-level fact ONCE, as an "i" tooltip on the header
      // (portfolio3 .pf3-drift-info) — not as a native title on all eleven cells. The
      // chip is a span, not a button: it lives inside the sort button, and nested
      // buttons are invalid HTML. Clicks on it are swallowed so reading ≠ re-sorting.
      label: (
        <span className="holdings-drift__head">
          {t('portfolio.byFund.colDrift')}
          <Tooltip
            side="bottom"
            content={
              <div className="holdings-drift__tip">
                <span className="holdings-drift__tip-h">{t('portfolio.byFund.colDrift')}</span>
                <span className="holdings-drift__tip-p">{t('portfolio.byFund.driftTip')}</span>
                <span className="holdings-drift__tip-p holdings-drift__tip-p--sub">
                  {t('portfolio.byFund.driftTipFormula', { pts: DRIFT_THRESHOLD_PCT })}
                </span>
              </div>
            }
          >
            <span
              className="holdings-drift__info"
              role="img"
              aria-label={t('portfolio.byFund.driftTip')}
              onClick={(e) => e.stopPropagation()}
            >
              i
            </span>
          </Tooltip>
        </span>
      ),
      numeric: true,
      render: (v, row) =>
        row._total ? (
          <span className="holdings-drift__na">—</span>
        ) : (
          <DriftCell value={v} max={maxDrift} hot={v !== null && Math.abs(v) >= DRIFT_THRESHOLD_PCT} />
        ),
    },
    {
      // Rate of return per holding (design: portfolio3 "Your ROR" — the value is
      // the ror the adapter maps into `ytd`, not the fund's published YTD).
      key: 'ytd',
      label: <span className="holdings-th--2line">{t('portfolio.byFund.colRor')}</span>,
      numeric: true,
      render: (v, row) => (v == null ? '—' : <PerfNum value={v} digits={1} arrow={!row._total} />),
    },
    {
      key: 'expense',
      label: t('portfolio.byFund.colExpense'),
      numeric: true,
      render: (v) => (v == null ? '—' : <span className="holdings-td-soft">{v.toFixed(2)}%</span>),
    },
  ];

  const classOptions = [
    { value: ALL, label: t('portfolio.byFund.allClasses') },
    ...assetClasses.map((c) => ({ value: c.key, label: c.label })),
  ];
  const familyOptions = [
    { value: ALL, label: t('portfolio.byFund.allFamilies') },
    ...families.map((f) => ({ value: f, label: f })),
  ];

  const handleExport = () => {
    const header = ['Fund', 'Ticker', 'Asset class', 'Model', 'Balance', 'Current alloc %', 'Future alloc %', 'Drift pts', 'Rate of return %', 'Expense %'];
    const body = visibleFunds.map((f) => [
      f.name,
      f.ticker,
      f.assetClass,
      f.modelName || '',
      f.balance,
      balanceText(f),
      electionText(f, plan),
      driftText(f, plan),
      f.ytd,
      f.expense,
    ]);
    const csv = [header, ...body].map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'holdings-by-fund.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setClassFilter(ALL);
    setFamilyFilter(ALL);
  };

  return (
    <div className="by-fund">
      {mobile ? (
        <div className="holdings-toolbar holdings-toolbar--mobile">
          <button
            type="button"
            className={cx('holdings-toolbar__filterbtn', activeCount && 'is-active')}
            onClick={() => setSheetOpen(true)}
          >
            <FunnelIcon width={16} height={16} /> {t('portfolio.byFund.filters')}
            {activeCount > 0 && <span className="holdings-toolbar__badge">{activeCount}</span>}
          </button>
          <button
            type="button"
            className="holdings-toolbar__export holdings-toolbar__export--icon"
            onClick={handleExport}
            aria-label={t('portfolio.byFund.exportCsv')}
            title={t('portfolio.byFund.exportCsv')}
          >
            <DownloadIcon width={16} height={16} />
          </button>
        </div>
      ) : (
        <div className="holdings-toolbar">
          <FilterDropdown
            label={t('portfolio.byFund.assetClass')}
            value={classFilter}
            options={classOptions}
            onChange={setClassFilter}
          />
          <FilterDropdown
            label={t('portfolio.byFund.fundFamily')}
            value={familyFilter}
            options={familyOptions}
            onChange={setFamilyFilter}
          />
          <button type="button" className="holdings-toolbar__export" onClick={handleExport}>
            <DownloadIcon width={16} height={16} /> {t('portfolio.byFund.exportCsv')}
          </button>
        </div>
      )}

      <BottomSheet open={mobile && sheetOpen} onClose={() => setSheetOpen(false)}>
        <div className="holdings-sheet">
          <div className="holdings-sheet__head">
            <span className="holdings-sheet__title">{t('portfolio.byFund.filters')}</span>
            <button type="button" className="holdings-sheet__clear" onClick={clearAll}>
              {t('portfolio.byFund.clearAll')}
            </button>
          </div>
          <div className="holdings-sheet__body">
            <FilterDropdown
              label={t('portfolio.byFund.assetClass')}
              value={classFilter}
              options={classOptions}
              onChange={setClassFilter}
              menuClassName="holdings-filter__menu--sheet"
            />
            <FilterDropdown
              label={t('portfolio.byFund.fundFamily')}
              value={familyFilter}
              options={familyOptions}
              onChange={setFamilyFilter}
              menuClassName="holdings-filter__menu--sheet"
            />
          </div>
          <div className="holdings-sheet__foot">
            <button
              type="button"
              className="holdings-sheet__apply"
              onClick={() => setSheetOpen(false)}
            >
              {t('portfolio.byFund.show', {
                count: visibleFunds.length,
                unit: unit(visibleFunds.length),
              })}
            </button>
          </div>
        </div>
      </BottomSheet>

      <DataTable
        className={cx(
          'holdings-table holdings-table--fund data-table--no-collapse',
          // Long lineups scroll inside the card rather than stretching the page.
          topRows.length > SCROLL_AFTER_ROWS && 'holdings-table--scroll',
        )}
        columns={columns}
        rows={rows}
        sort={sort}
        onSort={setSort}
        rowKey={(r, i) => (r._child ? `${r._modelKey}:${r.ticker}` : r.key || r.ticker || i)}
        rowClassName={(r) =>
          cx(
            r._total && 'holdings-table__total-row',
            r._model && 'holdings-table__model-row',
            r._child && 'holdings-table__child-row',
            r._note && 'holdings-table__note-row',
          ) || undefined
        }
        onRowClick={(r) => {
          if (r._total || r._note) return;
          // A model row opens the model allocation modal when the page offers one (the
          // plus/minus toggle still expands it in place); otherwise the row toggles.
          if (r._model) (onPickModel ? onPickModel(r.key) : toggleModel(r.key));
          else onPickFund?.(r.ticker);
        }}
      />

      <p className="holdings-table__note">
        {singleHolding ? t('portfolio.byFund.singleHolding') : t('portfolio.byFund.note')}
        {!takesElections && ` ${t('portfolio.byFund.noElectionsNote')}`}
      </p>
    </div>
  );
}

ByFundTab.propTypes = {
  /** Flat investable fund list — drives the family filter and the fallback list. */
  funds: PropTypes.array,
  /** Model-aware holdings list from the adapter; falls back to a flat list. */
  tree: PropTypes.array,
  /** Plan record — its `actions` decide whether elections (and drift) apply. */
  plan: PropTypes.object,
  summary: PropTypes.object,
  holdings: PropTypes.object,
  assetClasses: PropTypes.array,
  onPickFund: PropTypes.func,
  /** (modelKey) — a model row was clicked; absent, the row toggles its funds instead. */
  onPickModel: PropTypes.func,
};

export default ByFundTab;
